import { useState } from 'react'
import { Alert, Card, Col, DatePicker, Descriptions, Row, Space, Statistic, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  downloadFinanceExport,
  fetchIncomeStatement,
  type IncomeStatement,
} from '@/features/finance/financeApi'
import { defaultDayRange } from '@/shared/lib/dayRange'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'
import { ReportScopeNoteBar } from '@/features/finance/ReportScopeNoteBar'
import { CostCenterFilter } from '@/features/finance/CostCenterFilter'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

/** Hisobotning bitta qatori — nom, summa va (ixtiyoriy) yakun belgisi */
interface StatementLine {
  key: string
  labelKey: string
  amountMinor: number
  /** Yakun qatori qalin chiziladi */
  emphasis?: boolean
  /** Manfiy son qizil emas, ODATIY: xarajat manfiy emas, u AYIRILADI */
  expense?: boolean
}

/**
 * Foyda hisoboti (P&L) — `GET /api/finance/reports/income-statement`.
 *
 * <p>Mijoz hujjatining §5.3 OLTITA bandi shu bitta ekranda: yalpi
 * daromad, tannarx (COGS), yalpi foyda, operatsion xarajat, sof foyda,
 * foyda marjasi. Backendda ham ular bitta so'rov — to'rttasi qolgan
 * ikkitasidan arifmetik chiqadi va alohida o'qilsa hisobot O'ZIGA ZID
 * bo'lishi mumkin edi ({@code QARORLAR.md} §12.2).
 *
 * <p>⚠️ Ilgari bu ma'lumot panelda FAQAT EKSPORT (fayl) sifatida
 * ochilardi — buxgalter foyda va zararni ko'rish uchun faylni yuklab
 * olishga majbur edi (`docs/11` §6).
 *
 * <h2>⚠️ BU RO'YXAT EMAS — SHAKL. SAHIFALASH, QIDIRUV VA SARALASH YO'Q</h2>
 * <p>Jadval OLTITA QATORDAN iborat va ular KOD ichida yozilgan
 * (`statementLines`): daromad, tannarx, yalpi foyda, operatsion
 * xarajat, boshqa xarajat, sof foyda. Bu buxgalteriya SHAKLI, ma'lumot
 * ro'yxati emas — shuning uchun ro'yxat qulayliklari bu yerga
 * QO'YILMADI va har biri aniq zarar keltirardi:
 * <ul>
 *   <li><b>Sahifalash</b> — `FinanceReportController#incomeStatement`
 *       da `page`/`size` YO'Q va olti qatorni sahifalashning ma'nosi
 *       ham yo'q;</li>
 *   <li><b>Saralash</b> — tartib arifmetikaning O'ZI: «daromad −
 *       tannarx = yalpi foyda» faqat shu ketma-ketlikda o'qiladi.
 *       Summa bo'yicha saralash hisobotni buzardi;</li>
 *   <li><b>Qidiruv/filtr</b> — bitta qatorni yashirish qolgan
 *       qatorlarni bir-biriga zid qilardi («yalpi foyda» bor, «tannarx»
 *       yo'q).</li>
 * </ul>
 * <p>Oyna tanlagichi (`RangePicker`) esa BOR va u shu sahifadagi yagona
 * ma'noli boshqaruv: hisobot NIMANI o'z ichiga olishini aynan u
 * belgilaydi.
 *
 * <h2>⚠️ PANEL ARIFMETIKANI TAKRORLAMAYDI</h2>
 * <p>Yalpi foyda, sof foyda va marjalar SERVERDAN kelgan holicha
 * ko'rsatiladi. Ularni brauzerda qayta hisoblash ikkinchi manba
 * yaratardi va bir kuni ikkisi ajralib ketardi.
 *
 * <h2>⚠️ MARJA NOL MAXRAJDA «—», «0%» EMAS</h2>
 * <p>Daromad nol bo'lsa server `null` yuboradi (maydon javobda umuman
 * kelmaydi — o'lchangan, Staging 2026-08-14). «0%» MA'NOLI javob
 * bo'lardi, holbuki savol qo'yilmagan: bo'linuvchi nol.
 *
 * <h2>⚠️ «TASNIFLANMAGAN» ALOHIDA KO'RSATILADI</h2>
 * <p>Foyda hisoboti ta'rifi bo'yicha faqat daromad va xarajatni
 * ko'radi; har provodkaning ikkinchi satri (bank, agent depoziti) unda
 * ko'rinmaydi. Uni yashirish «hamma pul harakati hisobga olindi» degan
 * yolg'on taassurot berardi.
 */
export function FinanceIncomeStatementPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs(defaultDayRange(0)[1]).startOf('month'),
    defaultDayRange(0)[1],
  ])
  const [nodeId, setNodeId] = useState<string | null>(null)

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.finance.incomeStatement(from, to, nodeId),
    queryFn: ({ signal }) => fetchIncomeStatement({ from, to, nodeId }, signal),
  })

  const data = report.data
  const currency = data?.note.currency ?? ''

  const lines: StatementLine[] = data ? statementLines(data) : []

  const columns: ColumnsType<StatementLine> = [
    {
      key: 'label',
      title: t('finance.incomeStatement.columns.item'),
      render: (_, row) => (
        <Typography.Text strong={row.emphasis}>{t(row.labelKey)}</Typography.Text>
      ),
    },
    {
      key: 'amount',
      align: 'right',
      title: t('finance.incomeStatement.columns.amount'),
      render: (_, row) => (
        <Typography.Text
          strong={row.emphasis}
          type={row.emphasis && row.amountMinor < 0 ? 'danger' : undefined}
        >
          {/* ⚠️ Xarajat qatorida «−» belgisi KO'RSATILADI: server musbat
              beradi (ishorani SQL bir marta ag'daradi), lekin hisobotda
              u AYIRILADI va buni operator ko'rishi kerak. */}
          {row.expense && row.amountMinor !== 0 ? '− ' : ''}
          {formatMoneyValue(money(row.amountMinor, currency))}
        </Typography.Text>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('finance.incomeStatement.title')}
        description={t('finance.incomeStatement.intro')}
        extra={
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('finance.incomeStatement.title')}
              disabled={report.isError}
              run={(format, onProgress) =>
                downloadFinanceExport('income-statement', format, { from, to, nodeId, onProgress })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">
            {t('finance.incomeStatement.rangeLabel')}:
          </Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
          <CostCenterFilter value={nodeId} onChange={setNodeId} />
        </Space>
        <ReportScopeNoteBar note={data?.note} />
      </Card>

      {data?.note.scopeRestricted ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.incomeStatement.scopeRestricted')}
        />
      ) : null}

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.incomeStatement.kpi.revenue')}
                  value={formatMoneyValue(money(data?.revenueMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.incomeStatement.kpi.grossProfit')}
                  value={formatMoneyValue(money(data?.grossProfitMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.incomeStatement.kpi.netProfit')}
                  valueStyle={(data?.netProfitMinor ?? 0) < 0 ? { color: '#cf1322' } : undefined}
                  value={formatMoneyValue(money(data?.netProfitMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                {/* ⚠️ `null` → «—». Sabab sahifa javadoc'ida */}
                <Statistic
                  title={t('finance.incomeStatement.kpi.netMargin')}
                  value={percentText(data?.netMarginPercent)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('finance.incomeStatement.kpi.grossMargin')}:{' '}
                  {percentText(data?.grossMarginPercent)}
                </Typography.Text>
              </Card>
            </Col>
          </Row>

          <Card title={t('finance.incomeStatement.tableTitle')} size="small">
            <Table<StatementLine>
              rowKey="key"
              columns={columns}
              dataSource={lines}
              loading={report.isPending}
              pagination={false}
              size="small"
              showHeader
            />
          </Card>

          {data?.consolidated && data.eliminations ? (
            <Card
              size="small"
              style={{ marginTop: 16 }}
              title={t('finance.incomeStatement.eliminations.title')}
            >
              <Typography.Paragraph type="secondary">
                {t('finance.incomeStatement.eliminations.hint')}
              </Typography.Paragraph>
              <Descriptions column={{ xs: 1, md: 3 }} size="small">
                <Descriptions.Item label={t('finance.incomeStatement.eliminations.internalSales')}>
                  {formatMoneyValue(money(data.eliminations.internalSalesMinor, currency))}
                </Descriptions.Item>
                <Descriptions.Item label={t('finance.incomeStatement.eliminations.internalCost')}>
                  {formatMoneyValue(money(data.eliminations.internalCostMinor, currency))}
                </Descriptions.Item>
                <Descriptions.Item label={t('finance.incomeStatement.eliminations.markupChange')}>
                  {formatMoneyValue(money(data.eliminations.markupChangeMinor, currency))}
                </Descriptions.Item>
              </Descriptions>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {data.eliminations.markupAsOf
                  ? t('finance.incomeStatement.eliminations.markupAsOf', {
                      date: data.eliminations.markupAsOf,
                    })
                  : t('finance.incomeStatement.eliminations.noMarkup')}
              </Typography.Text>
            </Card>
          ) : null}

          {/*
            ⚠️ «Tasniflanmagan» — hisobot ICHIDA emas, YONIDA. Uni
            qatorlar orasiga qo'yish «daromad − xarajat = sof foyda»
            tengligini buzardi: bu summa foyda hisobotiga TA'RIFI
            BO'YICHA kirmaydi.
          */}
          {data && data.unclassifiedMinor !== 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message={t('finance.incomeStatement.unclassified', {
                amount: formatMoneyValue(money(data.unclassifiedMinor, currency)),
              })}
              description={t('finance.incomeStatement.unclassifiedHint')}
            />
          ) : null}
        </>
      )}
    </>
  )
}

/**
 * Hisobot qatorlari — TARTIB buxgalteriya shaklidagidek.
 *
 * ⚠️ Qiymatlar SERVERDAN, hech biri bu yerda hisoblanmaydi
 * (`IncomeStatement#of` — arifmetikaning yagona joyi).
 */
function statementLines(data: IncomeStatement): StatementLine[] {
  return [
    { key: 'revenue', labelKey: 'finance.incomeStatement.lines.revenue', amountMinor: data.revenueMinor },
    { key: 'cogs', labelKey: 'finance.incomeStatement.lines.cogs', amountMinor: data.cogsMinor, expense: true },
    {
      key: 'grossProfit',
      labelKey: 'finance.incomeStatement.lines.grossProfit',
      amountMinor: data.grossProfitMinor,
      emphasis: true,
    },
    {
      key: 'operatingExpense',
      labelKey: 'finance.incomeStatement.lines.operatingExpense',
      amountMinor: data.operatingExpenseMinor,
      expense: true,
    },
    {
      key: 'otherExpense',
      labelKey: 'finance.incomeStatement.lines.otherExpense',
      amountMinor: data.otherExpenseMinor,
      expense: true,
    },
    {
      key: 'netProfit',
      labelKey: 'finance.incomeStatement.lines.netProfit',
      amountMinor: data.netProfitMinor,
      emphasis: true,
    },
  ]
}

/** Foiz matni — `null`/`undefined` da «—» (0% EMAS) */
function percentText(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(2)}%`
}
